/**
 * HQ › Bruce — who can use it. The owner's page for switching Bruce on or off
 * per person (Satya, 2026-09-19: "an HQ view only I can see, and from there I
 * will choose for whom Bruce should be visible").
 *
 * Who may open it is decided server-side (BRUCE_OWNER_PHONES); the sidebar
 * entry is hidden for everyone else, and a typed URL gets the notice below.
 * A switch writes one row; the person's next request reads it — no deploy.
 */
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { fetchBruceAccess, searchBruceAccess, setBruceAccess } from 'api'
import { color, radius, space } from 'app/theme/tokens'

const SEARCH_DELAY_MS = 250

const HOW_IT_WORKS =
  'Only you can see this page. Switch a person on and the Bruce button ' +
  'appears for them the next time they open or refresh Alfred. Switch them ' +
  'off and their questions are refused straight away; their past chats are ' +
  'kept. Answers only ever cover the centres shown for that person.'

const useStyles = makeStyles(() => ({
  root: {
    padding: space.lg
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: space.xs,
    marginBottom: space.md
  },
  searchWrap: {
    position: 'relative',
    maxWidth: 440,
    marginBottom: space.md
  },
  search: {
    background: color.surface
  },
  results: {
    position: 'absolute',
    top: 44,
    left: 0,
    right: 0,
    zIndex: 5,
    borderRadius: radius.md,
    overflow: 'hidden'
  },
  result: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.sm,
    padding: `${space.sm} ${space.md}`,
    borderTop: `1px solid ${color.border}`,
    '&:first-child': { borderTop: 'none' }
  },
  resultAction: {
    border: 'none',
    background: 'none',
    color: color.brand,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    padding: `${space.xs} ${space.sm}`,
    '&:disabled': { color: color.textFaint, cursor: 'default' }
  },
  card: {
    borderRadius: radius.md,
    border: `1px solid ${color.border}`,
    overflowX: 'auto'
  },
  table: {
    minWidth: 760
  },
  th: {
    fontSize: 12,
    fontWeight: 600,
    color: color.textMuted,
    whiteSpace: 'nowrap'
  },
  meta: {
    fontSize: 12,
    color: color.textMuted
  },
  you: {
    display: 'inline-block',
    marginLeft: space.sm,
    padding: `0 ${space.sm}`,
    borderRadius: radius.pill,
    background: color.brandSoft,
    color: color.ink,
    fontSize: 11,
    fontWeight: 600
  },
  switchCell: {
    whiteSpace: 'nowrap'
  },
  switchLabel: {
    fontSize: 12,
    color: color.textMuted
  },
  error: {
    color: color.negative,
    marginBottom: space.sm
  },
  notice: {
    padding: space.lg
  }
}))

const whenAsked = (iso) => {
  if (!iso) return 'Never asked'
  const at = dayjs(iso)
  if (at.isSame(dayjs(), 'day')) return `Today, ${at.format('HH:mm')}`
  if (at.isSame(dayjs().subtract(1, 'day'), 'day')) {
    return `Yesterday, ${at.format('HH:mm')}`
  }
  return at.format('D MMM YYYY')
}

const switchLabel = (person) => {
  if (person.is_owner) return 'Always on (you)'
  const since = person.changed_at
    ? ` since ${dayjs(person.changed_at).format('D MMM')}`
    : ''
  return person.enabled ? `On${since}` : `Off${since}`
}

const resultAction = (person) => {
  if (person.is_owner) return { label: 'You — always on', disabled: true }
  if (person.enabled) return { label: 'Already on', disabled: true }
  return { label: '+ Switch on', disabled: false }
}

const BruceAccess = () => {
  const classes = useStyles()
  const [people, setPeople] = useState(null)
  const [windowDays, setWindowDays] = useState(28)
  const [forbidden, setForbidden] = useState(false)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  // The query the current results answer — so "no match" never shows while a
  // search is still on its way.
  const [answered, setAnswered] = useState('')
  const [busy, setBusy] = useState('')
  const searchSeq = useRef(0)

  const load = useCallback(async () => {
    try {
      const data = await fetchBruceAccess()
      setPeople(data.people || [])
      setWindowDays(data.window_days || 28)
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true)
      else setError("Couldn't load the list right now — try again in a moment.")
      setPeople([])
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) {
      setResults([])
      return undefined
    }
    const seq = searchSeq.current + 1
    searchSeq.current = seq
    const timer = setTimeout(() => {
      searchBruceAccess(q)
        .then((data) => {
          if (searchSeq.current !== seq) return
          setResults(data.results || [])
          setAnswered(q)
        })
        .catch(() => {
          if (searchSeq.current === seq) setResults([])
        })
    }, SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [query])

  const flip = async (person, enabled) => {
    setBusy(person.user)
    setError('')
    try {
      const data = await setBruceAccess(person.user, enabled)
      const updated = data.person
      setPeople((rows) => {
        const known = rows.some((r) => r.user === updated.user)
        return known
          ? rows.map((r) => (r.user === updated.user ? updated : r))
          : [
              ...rows.filter((r) => r.is_owner),
              updated,
              ...rows.filter((r) => !r.is_owner)
            ]
      })
      if (enabled) {
        setQuery('')
        setResults([])
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          `Couldn't switch ${person.name} ${
            enabled ? 'on' : 'off'
          } — try again.`
      )
    } finally {
      setBusy('')
    }
  }

  if (forbidden) {
    return (
      <Box className={classes.notice}>
        <Typography variant="h6">Bruce — who can use it</Typography>
        <Typography variant="body2" color="textSecondary">
          Only Bruce&apos;s owner can open this page. Ask Satya if someone needs
          Bruce switched on.
        </Typography>
      </Box>
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.head}>
        <Typography variant="h5">Bruce — who can use it</Typography>
        <Tooltip title={HOW_IT_WORKS}>
          <IconButton size="small" aria-label="How this page works">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.searchWrap}>
        <TextField
          className={classes.search}
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Add a person: type a name or phone"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {results.length > 0 && (
          <Paper className={classes.results} elevation={4}>
            {results.map((r) => {
              const action = resultAction(r)
              return (
                <div key={r.user} className={classes.result}>
                  <div>
                    <div>{r.name}</div>
                    <div className={classes.meta}>
                      {r.role} · {r.covers}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={classes.resultAction}
                    disabled={action.disabled || busy === r.user}
                    onClick={() => flip(r, true)}
                  >
                    {action.label}
                  </button>
                </div>
              )
            })}
          </Paper>
        )}
        {query.trim().length >= 2 &&
          answered === query.trim() &&
          results.length === 0 && (
            <div className={classes.meta} style={{ marginTop: space.xs }}>
              No Alfred user matches &ldquo;{query.trim()}&rdquo; yet.
            </div>
          )}
      </div>

      {error && <div className={classes.error}>{error}</div>}

      {people === null ? (
        <Box display="flex" alignItems="center">
          <CircularProgress size={22} />
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginLeft: 12 }}
          >
            Loading…
          </Typography>
        </Box>
      ) : (
        <Paper className={classes.card} elevation={0}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.th}>Person</TableCell>
                <TableCell className={classes.th}>Answers cover</TableCell>
                <TableCell className={classes.th}>Bruce</TableCell>
                <TableCell className={classes.th}>Last question</TableCell>
                <TableCell className={classes.th}>
                  Questions, last {windowDays} days
                </TableCell>
                <TableCell className={classes.th}>👍 / 👎</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {people.map((p) => (
                <TableRow key={p.user}>
                  <TableCell>
                    <b>{p.name}</b>
                    {p.is_owner && <span className={classes.you}>you</span>}
                    <div className={classes.meta}>{p.role}</div>
                  </TableCell>
                  <TableCell>{p.covers}</TableCell>
                  <TableCell className={classes.switchCell}>
                    <Switch
                      size="small"
                      color="primary"
                      checked={p.enabled}
                      disabled={p.is_owner || busy === p.user}
                      onChange={(e) => flip(p, e.target.checked)}
                      inputProps={{ 'aria-label': `Bruce for ${p.name}` }}
                    />
                    <span className={classes.switchLabel}>
                      {switchLabel(p)}
                    </span>
                  </TableCell>
                  <TableCell>{whenAsked(p.last_question_at)}</TableCell>
                  <TableCell>{p.questions}</TableCell>
                  <TableCell>
                    {p.up} / {p.down}
                  </TableCell>
                </TableRow>
              ))}
              {people.filter((p) => !p.is_owner).length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className={classes.meta}>
                    Nobody else has Bruce yet. Search above to switch someone
                    on.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </div>
  )
}

export default BruceAccess
